/**
 * Trovva — Agent Retention Risk Widget (Broker Dashboard)
 *
 * Shows agent churn risk scores with color-coded severity levels.
 * Fetches from GET /api/attrition-scores on mount.
 * Falls back to demo data if unavailable.
 */

import { useState, useEffect } from 'react';
import { AlertTriangle, Users, TrendingDown } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { theme } from '../../lib/theme';

// Demo data for fallback
const DEMO_ATTRITION = {
  agents: [
    { id: 1, name: 'Sylvia Micarelli', risk_score: 87, risk_level: 'critical' },
    { id: 2, name: 'Desiree Hogbin', risk_score: 72, risk_level: 'at_risk' },
    { id: 3, name: 'Jenifer Raffa', risk_score: 61, risk_level: 'at_risk' },
    { id: 4, name: 'Patricia Furrow', risk_score: 28, risk_level: 'watch' },
    { id: 5, name: 'Neha', risk_score: 15, risk_level: 'low' },
  ],
};

// Color map by risk level
const RISK_COLORS = {
  low: '#22c55e',
  watch: '#f59e0b',
  at_risk: '#f97316',
  critical: '#ef4444',
};

function getRiskBadgeColor(level) {
  return RISK_COLORS[level] || theme.colors.info;
}

export default function AttritionWidget({ user, org }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAttritionScores = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          setData(DEMO_ATTRITION);
          setLoading(false);
          return;
        }

        const response = await fetch('/api/attrition-scores', {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });

        if (response.ok) {
          const result = await response.json();
          setData(result);
        } else {
          setData(DEMO_ATTRITION);
        }
      } catch (err) {
        console.error('Failed to fetch attrition scores:', err);
        setData(DEMO_ATTRITION);
      }
      setLoading(false);
    };

    fetchAttritionScores();
  }, []);

  if (loading) {
    return (
      <div style={{
        background: theme.colors.card, borderRadius: 12,
        border: `1px solid ${theme.colors.border}`,
        padding: 20, maxHeight: 280,
      }}>
        <div style={{ fontSize: 13, color: theme.colors.textLight, textAlign: 'center' }}>
          Loading...
        </div>
      </div>
    );
  }

  const agents = (data?.agents || []).sort((a, b) => (b.risk_score || 0) - (a.risk_score || 0));
  const totalAgents = agents.length;
  const atRiskCount = agents.filter(a => a.risk_level === 'at_risk' || a.risk_level === 'critical').length;
  const criticalCount = agents.filter(a => a.risk_level === 'critical').length;

  const topAgents = agents.slice(0, 5);

  return (
    <div style={{
      background: theme.colors.card, borderRadius: 12,
      border: `1px solid ${theme.colors.border}`,
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        padding: '12px 16px', borderBottom: `1px solid ${theme.colors.border}`,
        display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <AlertTriangle size={14} color={theme.colors.warning} />
        <span style={{ fontSize: 13, fontWeight: 700, color: theme.colors.text }}>
          Agent Retention Risk
        </span>
      </div>

      {/* Summary row */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 12, padding: '12px 16px',
        background: theme.colors.bg,
        borderBottom: `1px solid ${theme.colors.border}`,
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: theme.colors.text }}>
            {totalAgents}
          </div>
          <div style={{ fontSize: 10, color: theme.colors.textLight }}>Total Agents</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: theme.colors.warning }}>
            {atRiskCount}
          </div>
          <div style={{ fontSize: 10, color: theme.colors.textLight }}>At Risk</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: theme.colors.danger }}>
            {criticalCount}
          </div>
          <div style={{ fontSize: 10, color: theme.colors.textLight }}>Critical</div>
        </div>
      </div>

      {/* Agent list with scrollable container */}
      <div style={{
        maxHeight: 210, overflowY: 'auto',
        padding: 0,
      }}>
        {topAgents.length === 0 ? (
          <div style={{ padding: '20px 16px', textAlign: 'center' }}>
            <div style={{
              width: 40, height: 40, borderRadius: 12,
              background: `${theme.colors.success}10`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 10px',
            }}>
              <Users size={20} color={theme.colors.success} />
            </div>
            <div style={{ fontSize: 12, fontWeight: 600, color: theme.colors.text, marginBottom: 4 }}>
              All agents healthy
            </div>
            <div style={{ fontSize: 11, color: theme.colors.textLight }}>
              No retention risks detected
            </div>
          </div>
        ) : (
          topAgents.map((agent, idx) => (
            <div
              key={agent.id || idx}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px',
                borderBottom: idx < topAgents.length - 1 ? `1px solid ${theme.colors.border}40` : 'none',
              }}
            >
              {/* Agent name */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: theme.colors.text }}>
                  {agent.name}
                </div>

                {/* Risk bar */}
                <div style={{
                  marginTop: 6, width: '100%', height: 6, borderRadius: 3,
                  background: theme.colors.border, overflow: 'hidden',
                }}>
                  <div style={{
                    width: `${agent.risk_score || 0}%`,
                    height: '100%',
                    background: getRiskBadgeColor(agent.risk_level),
                    transition: 'width 0.3s ease',
                  }} />
                </div>
              </div>

              {/* Risk score + badge */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                <div style={{
                  fontSize: 11, fontWeight: 700, color: theme.colors.text,
                  minWidth: 28, textAlign: 'right',
                }}>
                  {agent.risk_score || 0}%
                </div>
                <span style={{
                  fontSize: 9, fontWeight: 700, padding: '3px 7px', borderRadius: 4,
                  background: `${getRiskBadgeColor(agent.risk_level)}12`,
                  color: getRiskBadgeColor(agent.risk_level),
                  whiteSpace: 'nowrap',
                  textTransform: 'uppercase',
                }}>
                  {agent.risk_level || 'unknown'}
                </span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
